'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Smartphone } from 'lucide-react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Button } from '../ui/button';
import { NavLink } from '../ui/nav-link';

import MobileSidebar from './mobile-sidebar';
import { Logo } from '@eliadmin/core/src/components/ui/logo';
import { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';

/**
 * Hlavička KAPYCAR — Figma:
 *   desktop (nad hero)   `10:4`      výška 100, rámec 1408 so zvislými linkami
 *   desktop (sticky)     `54:3613`   výška  80, BEZ rámca, tieň 0 0 90 rgba(0,0,0,.08)
 *   mobil (zatvorený)    `60042:5883` výška 58, vľavo menu+logo, vpravo „Zavolať"
 *   mobil (otvorený)     `60042:9400` menu ikona → krížik, pod hlavičkou panel
 *
 * Sticky sa podľa zadania prepína po zascrollovaní o 20px.
 *
 * Prečo `fixed` + rozperka namiesto `sticky`: sticky prvok si v toku drží svoju
 * pôvodnú výšku, takže zmena 100 → 80 by pri zaseknutí posunula celý obsah pod
 * ním o 20px. Fixed hlavička s rozperkou pevnej výšky sa takto nesprá.
 *
 * Farby: jediná premenná je `primary` (#1A5FD6), zvyšok natvrdo hexom.
 */

const SCROLL_THRESHOLD = 20;

export interface SiteHeaderProps {
  /** Položky hlavného menu z administrácie (menu `main_type: header`). */
  items: { name: string; path: string }[];
  /** Telefón z administrácie; keď je prázdny, použije sa `fallbackPhone`. */
  phone: string;
  /** Logo z administrácie. Keď chýba, vykreslí sa textový nápis (viď Brand). */
  logoUrl?: string;
  appName: string;
  ctaLabel: string;
  ctaHref: string;
  generalLayout: GeneralLayout['data'];
}

export function SiteHeader({
  items,
  phone,
  logoUrl,
  appName,
  ctaLabel,
  ctaHref,
  generalLayout,
}: SiteHeaderProps) {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const telHref = `tel:${phone.replace(/\s/g, '')}`;
  const isActive = (path: string) => {
    // Kotvy (#o-nas, #sluzby …) sa podľa cesty rozlíšiť nedajú — aktívna by
    // vyžadovala scroll-spy. Zvýrazňuje sa preto len skutočná zhoda cesty.
    if (path.startsWith('#')) return false;
    return pathname === path || pathname === `/${path.replace(/^\//, '')}`;
  };

  return (
    <>
      {/* Rozperka drží miesto pod fixnou hlavičkou v jej NEZASCROLLOVANEJ výške. */}
      <div aria-hidden="true" className="h-[58px] lg:h-[100px]" />

      <header
        className={cn(
          'ea-header',
          'custom-section fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-300',
          // Nezascrollovaná hlavička je priehľadná, aby cez ňu prešli zvislé
          // linky rámca (kreslí ich .page-frame pod ňou). Podklad je aj tak
          // #F6F1EA, takže vizuálne je to to isté. Po zascrollovaní sa zaplní
          // a linky prekryje — presne ako sticky vo Figme, ktorý ich nemá.
          scrolled ? 'bg-[#F6F1EA]' : 'bg-transparent',
          scrolled && 'shadow-[0px_0px_90px_0px_rgba(0,0,0,0.08)]',
        )}
      >
        <div
          className={cn(
            'flex w-full items-center justify-between transition-all duration-300',
            // Zascrollovaná hlavička podľa Figmy rámec so zvislými linkami nemá
            // a obsah ide až po okraj rámca (160 od kraja).
            scrolled ? 'mx-auto max-w-[1408px] py-4 lg:py-[14px]' : 'custom-container py-4 lg:py-6',
          )}
        >
          <div className="flex items-center gap-4">
            {/* Mobilné menu — vysúva sa `side="top"` cez celú šírku a prekryje
                hlavičku, takže na desktope sa nezobrazuje vôbec. */}
            <div className="lg:hidden">
              <MobileSidebar
                side="top"
                menu={items}
                phone={phone}
                logoUrl={logoUrl}
                appName={appName}
                ctaLabel={ctaLabel}
                ctaHref={ctaHref}
              />
            </div>

            <Logo
              className={{ image: 'h-[21px] sm:h-6 lg:h-8 xl:h-10' }}
              generalLayout={generalLayout}
            />
          </div>

          {/* Navigácia — Figma ju na mobile presúva do panelu. */}
          <nav className="hidden items-center gap-6 lg:flex xl:gap-8">
            {items.map(item => (
              <NavLink
                key={item.path}
                title={item.name}
                href={item.path}
                active={isActive(item.path)}
              />
            ))}
          </nav>

          <div className="flex items-center gap-6 xl:gap-8">
            {/* Na mobile je z telefónu len skratka „Zavolať" (Figma 60042:5874).
                Keď je panel otvorený, Figma ju skrýva — telefón je vnútri panelu. */}
            <Link
              href={telHref}
              className={cn(
                'ea-header__phone flex items-center gap-2 text-[#212121] transition-opacity hover:opacity-70',
              )}
            >
              <Smartphone
                className="size-5 shrink-0 text-primary lg:size-[26px]"
                strokeWidth={2}
                aria-hidden="true"
              />
              <span className="nav-link whitespace-nowrap lg:hidden">Zavolať</span>
              <span className="nav-link hidden whitespace-nowrap lg:inline">{phone}</span>
            </Link>

            <Button
              asChild
              variant="brand-accent"
              size="brand"
              rounded="pill"
              className="hidden lg:inline-flex"
            >
              <Link href={ctaHref}>{ctaLabel}</Link>
            </Button>
          </div>
        </div>
      </header>
    </>
  );
}

/**
 * Logo. Keď v administrácii ešte nie je nahraté (dnes nie je), vykreslí sa
 * textový nápis v rovnakej mierke ako vo Figme — obrázky nahráva David.
 */
function Brand({ logoUrl, appName }: { logoUrl?: string; appName: string }) {
  return (
    <Link
      href={ROUTES.HOME()}
      className="ea-header__logo flex items-center transition-opacity hover:opacity-75"
    >
      {logoUrl ? (
        <span className="relative block h-[21px] w-[132px] md:h-[28px] md:w-[178px] lg:h-[32px] lg:w-[204px] xl:h-[40px] xl:w-[255px]">
          <Image
            src={logoUrl}
            alt={appName}
            fill
            sizes="255px"
            className="object-contain object-left"
          />
        </span>
      ) : (
        <span className="text-[21px] font-black uppercase leading-none tracking-[-0.02em] text-[#212121] md:text-[28px] lg:text-[32px] xl:text-[40px]">
          Kapycar<span className="text-primary">.</span>
        </span>
      )}
    </Link>
  );
}
